import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { taskViewStateApi } from 'entities/task';
import { TaskBoardSkeleton } from 'widgets/TaskBoard/ui/BoardTaskSkeleton';
import { TaskListSkeleton } from 'widgets/TaskList/ui/TaskListSkeleton';

import { GoToTop } from '@features/GoToTop';
import { Tabs } from 'components/newUI/Tabs/Tabs';
import { fetchTasks } from 'entities/task/api';
import { TasksModal } from 'widgets/Modals';
import { TaskHeader } from 'widgets/TaskHeader/ui';

import cls from './index.module.css';
import { useAppDispatch, useAppSelector } from '../../../store';
import { taskBoardActions } from '../../../store/slices/taskBoardSlice/taskBoardSlice';
import { useTaskBoard } from '../hooks/useTaskBoard';

interface TaskBoardProps {
    classes?: string;
    fillAvailableHeight?: boolean;
    portalFileOverlays?: boolean;
}

export const TaskBoardPage = ({
    classes,
    fillAvailableHeight = false,
    portalFileOverlays = false,
}: TaskBoardProps) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { activeTab, onChangeActiveTab, tabsList, tabsContentMap } = useTaskBoard();
    const dispatch = useAppDispatch();
    const viewState = useQuery(taskViewStateApi.queryOptions());
    const started = useRef(false);
    const [tasksLoaded, setTasksLoaded] = useState(false);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const { isLoading, error } = useAppSelector((state) => state.tasksReducer);
    const onFiltersApplied = useCallback(() => setFiltersApplied(true), []);
    const queryParams = new URLSearchParams(location.search);
    const activeTaskId = queryParams.get('task_id');
    const isTaskExist = useAppSelector((store) => store.tasksReducer.entities[activeTaskId]);

    useEffect(() => {
        if (!viewState.data || started.current) return;
        started.current = true;
        const isNeedGanttTasks = viewState.data.filters.type.some((type) => type === 'gantt')
            ? 1
            : 0;
        dispatch(fetchTasks(isNeedGanttTasks)).then((action) => {
            setTasksLoaded(fetchTasks.fulfilled.match(action));
        });
    }, [dispatch, viewState.data]);

    // выводим модалку, если есть таб в url
    useEffect(() => {
        if (activeTaskId && isTaskExist && activeTab) {
            dispatch(taskBoardActions.onModalMode('editTask'));
        }
    }, [activeTaskId, isTaskExist, activeTab]);

    return (
        <div
            className={classNames(cls.TaskBoard, [classes], {
                [cls.FillAvailableHeight]: fillAvailableHeight,
            })}
        >
            <h2 className={cls.PageTitle}>{t('Pages.TaskBoard.pageTitle')}</h2>
            <Tabs onChange={onChangeActiveTab} tabsList={tabsList} activeTab={activeTab} />
            <TasksModal portalFileOverlays={portalFileOverlays} />
            {viewState.data && tasksLoaded && (
                <TaskHeader
                    initialViewState={viewState.data}
                    tasksLoaded={tasksLoaded}
                    onFiltersApplied={onFiltersApplied}
                />
            )}
            <GoToTop containerRef={containerRef} />
            <div className={cls.Content} ref={containerRef}>
                {viewState.isError ? (
                    <div role="alert">
                        {t('tasks.viewState.loadError')}
                        <button onClick={() => viewState.refetch()}>
                            {t('tasks.viewState.retry')}
                        </button>
                    </div>
                ) : error && !tasksLoaded ? (
                    <div role="alert">{t('tasks.viewState.tasksError')}</div>
                ) : !tasksLoaded || !filtersApplied || isLoading ? (
                    activeTab === 'taskBoard' ? (
                        <TaskBoardSkeleton />
                    ) : (
                        <TaskListSkeleton />
                    )
                ) : (
                    tabsContentMap[activeTab]
                )}
            </div>
        </div>
    );
};
